import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { InvestmentNegotiationService } from '../../service/rbi-compliance/investment-negotiation.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LoanSuggestedTermService } from '../../service/rbi-compliance/loan-suggested-term.service';
import { BorrowerDetail } from '../../model/finzy-flex/borrower.model';
import { LoanDetails } from '../../model/finzy-flex/loan-list.model';
import { InvestmentNegotiation } from '../../model/rbi-compliance/investment-negotiation.model';
import { LOAN_SUGGESTED_TERM, LoanSuggestedTerm, UserSuggestedTerm } from '../../model/rbi-compliance/loan-suggested-term.model';
import { Keys, StorageService } from '../../service/storage.service';
import { Toaster, Type } from '../../toaster/Toaster';

@Component({
  selector: 'app-loan-suggested-term-pop-up',
  templateUrl: './loan-suggested-term-pop-up.component.html',
  styleUrls: ['./loan-suggested-term-pop-up.component.css']
})
export class LoanSuggestedTermPopUpComponent implements OnInit {

  private readonly dialogRef: MatDialogRef<LoanSuggestedTermPopUpComponent>;
  private matDialogData: { loan: LoanDetails, borrowerDetail: BorrowerDetail, loanSuggestedTerm: LoanSuggestedTerm, isBorrower: boolean };
  public form: FormGroup;

  public data: any = {};

  constructor(
    @Inject(MAT_DIALOG_DATA) matDialogData: { loan: LoanDetails, borrowerDetail: BorrowerDetail, loanSuggestedTerm: LoanSuggestedTerm, isBorrower: boolean },
    dialogRef: MatDialogRef<LoanSuggestedTermPopUpComponent>,
    private readonly investmentNegotiationService: InvestmentNegotiationService,
    private readonly loanSuggestedTermService: LoanSuggestedTermService,
    private readonly storageService: StorageService,
    private formBuilder: FormBuilder,
  ) {
    this.dialogRef = dialogRef;
    this.matDialogData = matDialogData;
  }

  ngOnInit(): void {
    this.getInvestmentNegotiationAttributes();
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      loanAmount: new FormControl({ value: "₹ " + this.data.loanAmount, disabled: true }),
      currentTenure: new FormControl({ value: this.data.currentTenure + ' m', disabled: true }),
      currentROI: new FormControl({ value: this.data.currentROI + " p.a", disabled: true }),
      suggestedTenure: new FormControl(null, Validators.required),
      suggestedROI: new FormControl(null, Validators.required),
      remark: new FormControl(null),
    });

    if (this.matDialogData.isBorrower) {
      const lenderLoanTenureValue = this.data.lenderLoanTenure
        ? this.data.lenderLoanTenure + "m"
        : "";
      const lenderInterestRateValue = this.data.lenderInterestRate
        ? this.data.lenderInterestRate + "% p.a"
        : "";
      this.form.addControl(
        "lenderLoanTenure",
        new FormControl({ value: lenderLoanTenureValue, disabled: true })
      );
      this.form.addControl(
        "lenderInterestRate",
        new FormControl({ value: lenderInterestRateValue, disabled: true })
      );
    }
  }

  public getInvestmentNegotiationAttributes() {
    const { tenure, finzyRating } = this.matDialogData.loan;
    this.investmentNegotiationService.getInvestmentNegotiation()
      .then((res) => {
        const response = this.findTenureAndInterestRate(tenure, finzyRating, res[0]);
        this.data.loanAmount = this.matDialogData.loan.investmentAmount;
        this.data.currentTenure = this.matDialogData.loan.tenure;
        this.data.currentROI = response.currentInterestRate;
        this.data.suggestedTenure = response.tenure;
        this.data.suggestedROI = response.interestRate;
        if (this.matDialogData.loanSuggestedTerm && this.matDialogData.loanSuggestedTerm.userSuggestedTerms[0]) {
          this.data.lenderInterestRate = this.matDialogData.loanSuggestedTerm.userSuggestedTerms[0].lenderSuggestedRate;
          this.data.lenderLoanTenure = this.matDialogData.loanSuggestedTerm.userSuggestedTerms[0].lenderSuggestedTenure;
          this.data.borrowerLoanRate = this.matDialogData.loanSuggestedTerm.userSuggestedTerms[0].borrowerSuggestedRate;
          this.data.borrowerLoanTenure = this.matDialogData.loanSuggestedTerm.userSuggestedTerms[0].borrowerSuggestedTenure;
        }
        this.initForm();
      })
      .catch((err) => {
        console.log(err);
      })
  }

  public findTenureAndInterestRate(loanTenure: number, loanRating: string, data: InvestmentNegotiation): { tenure: number[], interestRate: number[], currentInterestRate: number } {
    // Find tenure based on start and end
    const tenureData = data.tenure.find(tenure => loanTenure >= tenure.start && loanTenure <= tenure.end);
    const tenure = tenureData ? tenureData.value : null;

    // Find interest rate based on start
    const interestRateData = data.interestRate.find(rate => loanRating === rate.rating);
    const interestRate = interestRateData ? interestRateData.value : null;
    const currentInterestRate = interestRateData ? interestRateData.start : null;

    return { tenure, interestRate, currentInterestRate };
  }

  public resetForm(): void {
    this.form.reset();
  }

  public onClose(data?: any): void {
    if (!this.form.valid) {
      this.dialogRef.close(null);
    } else {
      this.dialogRef.close(data);
    }
    this.resetForm();
  }

  public onSubmit(): void {
    if (this.form.valid) {
      console.log("Form submitted successfully!", this.form.value);

      const formData = {
        ...this.form.value,
        lenderSuggestedRate: this.matDialogData.isBorrower ? this.data.lenderInterestRate : this.form.value.suggestedROI,
        lenderSuggestedTenure: this.matDialogData.isBorrower ? this.data.lenderLoanTenure : this.form.value.suggestedTenure,
        borrowerSuggestedRate: this.matDialogData.isBorrower ? this.form.value.suggestedROI : this.form.value.borrowerLoanRate,
        borrowerSuggestedTenure: this.matDialogData.isBorrower ? this.form.value.suggestedTenure : this.form.value.borrowerLoanTenure,
        currentTenure: this.data.currentTenure,
        currentROI: this.data.currentROI,
        loanAmount: this.data.loanAmount,
      };

      const { loanId, loanShortId, loanAmount, tenure, finzyRating, loanStatus, investmentAmount, borrowerId, portfolioId } = this.matDialogData.loan;
      const { name } = this.matDialogData.borrowerDetail;
      const lenderId = this.storageService.getItem(Keys.LENDER_ID);
      const lenderName = this.storageService.getItem(Keys.NAME);

      const userSuggestedTerm: UserSuggestedTerm = new UserSuggestedTerm(
        investmentAmount,
        lenderId,
        portfolioId,
        lenderName,
        formData.lenderSuggestedRate,
        formData.lenderSuggestedTenure,
        formData.borrowerSuggestedRate,
        formData.borrowerSuggestedTenure,
        LOAN_SUGGESTED_TERM.IN_PROGRESS,
        LOAN_SUGGESTED_TERM.IN_PROGRESS,
        formData.remark,
      )

      const loanSuggestedTerm: LoanSuggestedTerm = new LoanSuggestedTerm(
        loanId,
        loanShortId,
        name,
        borrowerId,
        loanAmount,
        this.data.currentROI,
        tenure,
        finzyRating,
        loanStatus,
        loanStatus,
        [userSuggestedTerm]
      )
      if (this.matDialogData.loanSuggestedTerm && this.matDialogData.loanSuggestedTerm?.userSuggestedTerms?.length) {
        const id = this.matDialogData.loanSuggestedTerm._id;
        userSuggestedTerm._id = this.matDialogData.loanSuggestedTerm.userSuggestedTerms[0]._id;
        this.userStatusManage(userSuggestedTerm);
        this.updateLoanSuggestedTerm(id, userSuggestedTerm);
      } else {
        this.createLoanSuggestedTerm(loanSuggestedTerm);
      }
    }
  }

  public createLoanSuggestedTerm(loanSuggestedTerm: LoanSuggestedTerm) {
    this.loanSuggestedTermService.createLoanSuggestedTerm(loanSuggestedTerm)
      .then((res) => {
        this.dialogRef.close(res);
        Toaster.toast('Created Loan Terms Suggestion', Type.Success);
      })
      .catch((error) => {
        console.log('ERROR ', error);
      })
  }

  public updateLoanSuggestedTerm(id: string, userSuggestedTerm: UserSuggestedTerm) {
    this.loanSuggestedTermService.updateLoanSuggestedTerm(id, userSuggestedTerm)
      .then((res) => {
        this.onClose(res);
        Toaster.toast('Updated Loan Terms Suggestion', Type.Success);
      })
      .catch((error) => {
        console.log('ERROR ', error);
      })
  }

  private userStatusManage(userSuggestedTerm: UserSuggestedTerm) {
    if (this.matDialogData.isBorrower && (userSuggestedTerm.borrowerStatus === LOAN_SUGGESTED_TERM.IN_PROGRESS)) {
      userSuggestedTerm.borrowerStatus = LOAN_SUGGESTED_TERM.MODIFIED;
    } else if((userSuggestedTerm.lenderStatus === LOAN_SUGGESTED_TERM.IN_PROGRESS) || userSuggestedTerm.lenderStatus === LOAN_SUGGESTED_TERM.MODIFIED){
      userSuggestedTerm.lenderStatus = LOAN_SUGGESTED_TERM.MODIFIED;
      userSuggestedTerm.borrowerStatus = this.matDialogData.loanSuggestedTerm.userSuggestedTerms[0].borrowerStatus;
    }
  }

}
